import moment from 'moment';
import DateUtil from '../utils/DateUtil';
import {TSAnlaufstelleType} from './enums/TSAnlaufstelleType';
import {TSKantonCode} from './enums/TSKantonCode';
import {IHasUnconnectedDocuments} from './IHasUnconnectedDocuments';
import TSAbstractEntity from './TSAbstractEntity';
import TSAdresse from './TSAdresse';
import TSBenutzer from './TSBenutzer';
import TSGemeinde from './TSGemeinde';
import {TSSerializable} from './TSSerializable';

export default class TSAnlaufstelle extends TSAbstractEntity implements TSSerializable<TSAnlaufstelle>, IHasUnconnectedDocuments {

    private _name: string;

    private _abteilung: string | undefined;

    private _adresse: TSAdresse;

    private _active: boolean;

    private _anlaufstelleType: TSAnlaufstelleType;

    private _vereinbarungAkzeptiert: moment.Moment | undefined;

    private _verwalter: TSBenutzer | undefined;

    private _kanton: TSKantonCode | undefined;

    private _gemeinden: Array<TSGemeinde>;

    private _documentIdsToConnect: Array<string> = [];

    private readonly maximumLengthOfGemeindeList = 120;

    public get jurisdiction(): string {
        switch (this._anlaufstelleType) {
            case TSAnlaufstelleType.KANTON:
                return this._kanton ? this._kanton.toString() + ` (${this._name})` : `${this.name}`;
            case TSAnlaufstelleType.GEMEINDE:
                if (this._gemeinden) {
                    let blkStr: Array<String> = [];
                    this._gemeinden.forEach(g => blkStr.push(g.name));
                    let result = blkStr.join(', ');
                    if (result.length > this.maximumLengthOfGemeindeList) {
                        result = result.slice(0, this.maximumLengthOfGemeindeList - 1) + '...';
                    }
                    return result + ` (${this._name})`;
                }
                return `${this.name}`;
        }
        return `${this.name}`;
    }

    public get name(): string {
        return this._name;
    }

    public get kantonOrKantoneFromGemeinden(): string {
        if (this._kanton) {
            return this._kanton.toString();
        } else {
            return this._gemeinden.map(gem => gem.kanton).join(', ');
        }
    }

    public set name(value: string) {
        this._name = value;
    }

    public get adresse(): TSAdresse {
        return this._adresse;
    }

    public set adresse(value: TSAdresse) {
        this._adresse = value;
    }

    public get active(): boolean {
        return this._active;
    }

    public set active(value: boolean) {
        this._active = value;
    }

    public get anlaufstelleType(): TSAnlaufstelleType {
        return this._anlaufstelleType;
    }

    public set anlaufstelleType(value: TSAnlaufstelleType) {
        this._anlaufstelleType = value;
    }

    public get vereinbarungAkzeptiert(): moment.Moment | undefined {
        return this._vereinbarungAkzeptiert;
    }

    public set vereinbarungAkzeptiert(value: moment.Moment | undefined) {
        this._vereinbarungAkzeptiert = value;
    }

    public static deserializeArray(data: any): Array<TSAnlaufstelle> {
        let anlaufstelleArray: Array<TSAnlaufstelle> = [];
        if (data && Array.isArray(data)) {
            for (let i = 0; i < data.length; i++) {
                anlaufstelleArray[i] = new TSAnlaufstelle().deserialize(data[i]);
            }
        } else {
            anlaufstelleArray[0] = new TSAnlaufstelle().deserialize(data);
        }
        return anlaufstelleArray;
    }

    public get verwalter(): TSBenutzer | undefined {
        return this._verwalter;
    }

    public set verwalter(value: TSBenutzer | undefined) {
        this._verwalter = value;
    }

    public get kanton(): TSKantonCode | undefined {
        return this._kanton;
    }

    public set kanton(value: TSKantonCode | undefined) {
        this._kanton = value;
    }

    public get gemeinden(): Array<TSGemeinde> {
        return this._gemeinden;
    }

    public set gemeinden(value: Array<TSGemeinde>) {
        this._gemeinden = value;
    }

    public get abteilung(): string | undefined {
        return this._abteilung;
    }

    public set abteilung(value: string | undefined) {
        this._abteilung = value;
    }

    deserialize(restObject: any): TSAnlaufstelle {
        super.deserialize(restObject);

        this.name = restObject.name;
        this.abteilung = restObject.abteilung;
        this.adresse = this.deserializeOrCreate(restObject.adresse, new TSAdresse());
        this.active = restObject.active;
        this.anlaufstelleType = restObject.anlaufstelleType;
        this.vereinbarungAkzeptiert = DateUtil.localDateToMoment(restObject.vereinbarungAkzeptiert);
        this.verwalter = this.deserializeOrCreate(restObject.verwalter, new TSBenutzer());

        this.kanton = restObject.kanton;
        if (restObject.gemeinden) {
            this.gemeinden = restObject.gemeinden.map(TSGemeinde.getDeserializer);
        }
        return this;
    }

    serialize(restObject: any): any {
        super.serialize(restObject);

        restObject.name = this.name;
        restObject.abteilung = this.abteilung;
        restObject.adresse = this.serializeOrInit(this.adresse, restObject.adresse);
        restObject.active = this.active;
        restObject.anlaufstelleType = this.anlaufstelleType;
        if (this.vereinbarungAkzeptiert) {
            restObject.vereinbarungAkzeptiert = DateUtil.momentToLocalDate(this.vereinbarungAkzeptiert);
        }
        restObject.verwalter = this.serializeOrInit(this.verwalter, restObject.verwalter);
        restObject.kanton = this.kanton;
        restObject.gemeinden = this.arrayToRestObject(this.gemeinden);

        return restObject;
    }

    public get documentIdsToConnect(): Array<string> {
        return this._documentIdsToConnect;
    }

    public set documentIdsToConnect(value: Array<string>) {
        this._documentIdsToConnect = value;
    }

    public isInvalidVereinbarung(numberOfYearsValid: number): boolean {
        if (this.vereinbarungAkzeptiert === undefined
            || this.vereinbarungAkzeptiert === null) {
            return true;
        } else {
            let ablaufdatum = moment(this.vereinbarungAkzeptiert).add(numberOfYearsValid, 'years');
            return ablaufdatum.isBefore(DateUtil.now());
        }
    }

    public static getDeserializer = (anlaufstelle: any) => {
        return new TSAnlaufstelle().deserialize(anlaufstelle);
    };

}



// WEBPACK FOOTER //
// ./src/app/models/TSAnlaufstelle.ts